import "./Measurement.css";
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FormattedMessage } from "react-intl";
import { getAllcompanies } from "../../redux/apiCalls/companiesCall/companiesCall";
import { getCompany } from "../../redux/apiCalls/companiesCall/editCompanyCall";
import { ChevronRight, Menu } from "lucide-react";

const toText = (v) => {
  if (v == null) return "";
  if (typeof v === "string" || typeof v === "number") return String(v);
  if (typeof v === "object") return v.name ?? v.nombre ?? v.label ?? "";
  return "";
};

const DragDropCompanies = ({ setFirstSelect, firstSelect, maxSelections }) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.currentUser);

  const [searchTerm, setSearchTerm] = useState("");

  const [selectedCompanies, setSelectedCompanies] = useState(() => {
    try {
      const stored = localStorage.getItem("measurement_firstSelect");
      if (stored) {
        const parsed = JSON.parse(stored);
        if (maxSelections && parsed.length > maxSelections) {
          return parsed.slice(0, maxSelections);
        }
        return parsed;
      }
    } catch (e) {
      console.error(
        "Error parsing measurement_firstSelect from localStorage",
        e
      );
    }
    return Array.isArray(firstSelect) && firstSelect.length ? firstSelect : [];
  });

  const [companiesNames, setCompaniesNames] = useState({
    company: [],
    branch_offices: [],
    branch_office_place: [],
    place_zone: [],
    zone_local: [],
  });

  const [visible, setVisible] = useState({
    company: false,
    branch_offices: false,
    branch_office_place: false,
    place_zone: false,
    zone_local: false,
  });

  const toggleVisibility = (key) => {
    setVisible((prev) =>
      Object.fromEntries(
        Object.keys(prev).map((k) => [k, k === key ? !prev[k] : false])
      )
    );
  };

  useEffect(() => {
    async function fetchCompanies() {
      try {
        const [
          branchOffices,
          branchOfficePlace,
          placeZone,
          companies,
          zoneLocal,
        ] = await Promise.all([
          getAllcompanies(dispatch, user.accessToken, "branch_offices"),
          getAllcompanies(dispatch, user.accessToken, "branch_office_place"),
          getAllcompanies(dispatch, user.accessToken, "place_zone"),
          getAllcompanies(dispatch, user.accessToken, "company"),
          getAllcompanies(dispatch, user.accessToken, "zone_local"),
        ]);

        setCompaniesNames({
          company: companies || [],
          branch_offices: branchOffices || [],
          branch_office_place: branchOfficePlace || [],
          place_zone: placeZone || [],
          zone_local: zoneLocal || [],
        });
      } catch (error) {
        console.error("Error fetching companies:", error);
      }
    }

    if (user?.accessToken) {
      fetchCompanies();
    }
  }, [dispatch, user?.accessToken]);

  useEffect(() => {
    if (Array.isArray(firstSelect) && firstSelect.length > 0) {
      setSelectedCompanies(firstSelect);
      try {
        localStorage.setItem(
          "measurement_firstSelect",
          JSON.stringify(firstSelect)
        );
      } catch (e) {
        console.error("Error saving firstSelect to localStorage", e);
      }
    }
  }, [firstSelect]);

  useEffect(() => {
    try {
      if (Array.isArray(selectedCompanies) && selectedCompanies.length > 0) {
        localStorage.setItem(
          "measurement_firstSelect",
          JSON.stringify(selectedCompanies)
        );
      } else {
        localStorage.removeItem("measurement_firstSelect");
      }
    } catch (e) {
      console.error("Error persisting selectedCompanies to localStorage", e);
    }

    if (typeof setFirstSelect === "function") {
      setFirstSelect(selectedCompanies);
    }
  }, [selectedCompanies, setFirstSelect]);

  const handleFirstSelect = async (
    id,
    company_fk,
    company_name,
    currentLevel
  ) => {
    try {
      const res = await getCompany(
        dispatch,
        id,
        user.accessToken,
        currentLevel
      );

      if (!res) {
        console.error("getCompany devolvió vacío", {
          id,
          currentLevel,
          company_name,
        });
        return;
      }

      const keyByLevel = {
        company: "company_name",
        branch_offices: "office_name",
        branch_office_place: "place_name",
        place_zone: "zone_name",
        zone_local: "local_name",
      };

      const setId = currentLevel === "company" ? id : company_fk;
      const clickedKey = keyByLevel[currentLevel];
      const clickedLabel = toText(company_name);

      const namesParents = {
        ...(res.company_name && { company_name: toText(res.company_name) }),
        ...(res.office_name && { office_name: toText(res.office_name) }),
        ...(res.place_name && { place_name: toText(res.place_name) }),
        ...(res.zone_name && { zone_name: toText(res.zone_name) }),
        ...(res.local_name && { local_name: toText(res.local_name) }),
      };

      if (clickedKey) {
        namesParents[clickedKey] =
          clickedLabel || namesParents[clickedKey] || "";
      }

      const topCompanyName =
        toText(res.company_name) ||
        (clickedKey === "company_name" ? clickedLabel : "");

      const newCompany = {
        id,
        company_name: topCompanyName,
        id_company: setId,
        names_parents: namesParents,
        level: currentLevel,
      };

      setSelectedCompanies((prev) => {
        const exists = prev.some((c) => c.id === id);
        let updated;

        if (exists) {
          updated = prev.filter((c) => c.id !== id);
        } else {
          updated = [...prev, newCompany];
          if (maxSelections && updated.length > maxSelections) {
            updated = updated.slice(-maxSelections);
          }
        }

        return updated;
      });
    } catch (error) {
      console.error("Error en handleFirstSelect:", error);
    }
  };

  const handleRemoveCompany = (id) => {
    setSelectedCompanies((prev) => {
      const updated = prev.filter((c) => c.id !== id);

      if (updated.length === 0) {
        localStorage.removeItem("measurement_firstSelect");
      }

      if (typeof setFirstSelect === "function") {
        setFirstSelect(updated);
      }

      return updated;
    });
  };

  const sections = [
    { key: "branch_offices", label: "BranchOffices" },
    { key: "branch_office_place", label: "BranchOfficesPlace" },
    { key: "place_zone", label: "PlaceZone" },
    { key: "zone_local", label: "ZoneLocal" },
  ];

  const OptionList = ({ typeKey }) => {
    const list = companiesNames[typeKey] || [];
    const isSearching = searchTerm.trim().length > 0;

    const filteredList = list.filter((c) =>
      toText(c.name).toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (!isSearching && !visible[typeKey]) return null;
    if (isSearching && filteredList.length === 0) return null;

    return (
      <div className="options-wrapper">
        <li className="list-group-item list-group-item-action">
          {filteredList.map((c) => {
            const checked = selectedCompanies.some((sc) => sc.id === c.id);
            const labelText = toText(c.name);

            return (
              <div className="dropdown" key={`${typeKey}-${c.id}`}>
                <label
                  htmlFor={`opt-${typeKey}-${c.id}`}
                  className="company"
                  style={{ fontWeight: checked ? "bold" : "normal" }}
                  onClick={() =>
                    handleFirstSelect(
                      c.id,
                      c.company_fk ?? c.parent_organization,
                      labelText,
                      typeKey
                    )
                  }
                >
                  {labelText}
                </label>

                <input
                  id={`opt-${typeKey}-${c.id}`}
                  name={labelText}
                  type="checkbox"
                  checked={checked}
                  readOnly
                  style={{ display: "none" }}
                  className="company-checkbox"
                />
              </div>
            );
          })}
        </li>
      </div>
    );
  };

  return (
    <div className="toggleMediciones">
      <button
        className="iconConsumption"
        id="dropdownMenuButton1"
        data-bs-toggle="dropdown"
        aria-expanded="false"
        type="button"
      >
        <Menu size={18} />
        <span>PLANTAS</span>
      </button>

      {selectedCompanies.length > 0 && (
        <div className="company-tag-container company-tag">
          <div className="company-tag-container company-tag">
            {selectedCompanies.map((company) => {
              const np = company.names_parents || {};
              const path = [
                toText(np.company_name) || toText(company.company_name),
                toText(np.office_name),
                toText(np.place_name),
                toText(np.zone_name),
                toText(np.local_name),
              ].filter(Boolean);

              return (
                <span key={company.id} className="company-tag-selected">
                  {path.map((segment, index) => (
                    <React.Fragment key={`${company.id}-${segment}-${index}`}>
                      {index > 0 && (
                        <ChevronRight size={16} className="company-tag-arrow" />
                      )}
                      <span>{segment}</span>
                    </React.Fragment>
                  ))}
                  <span
                    className="company-tag-remove"
                    onClick={() => handleRemoveCompany(company.id)}
                    title="Quitar"
                  >
                    ×
                  </span>
                </span>
              );
            })}
          </div>
        </div>
      )}

      <ul
        className="dropdown-menu cont-drags"
        aria-labelledby="dropdownMenuButton1"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="search-box">
          <input
            type="text"
            placeholder="Buscar sucursal, zona, local..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        {sections.map(({ key, label }) => {
          const isSearching = searchTerm.trim().length > 0;
          const sectionMatches = (companiesNames[key] || []).filter((c) =>
            toText(c.name).toLowerCase().includes(searchTerm.toLowerCase())
          );

          return (
            <React.Fragment key={key}>
              {!isSearching && (
                <button
                  type="button"
                  data-name={key}
                  style={{ width: "100%" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleVisibility(key);
                  }}
                >
                  <FormattedMessage id={label} defaultMessage={label} />
                </button>
              )}

              {isSearching && sectionMatches.length > 0 && (
                <div className="search-section-title">
                  <FormattedMessage id={label} defaultMessage={label} />
                </div>
              )}

              <OptionList typeKey={key} />
            </React.Fragment>
          );
        })}
      </ul>
    </div>
  );
};

export default DragDropCompanies;